import React from 'react';

import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import { SidePage, Section } from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';

import AffiliateActions from 'scenes/Affiliates/components/Actions';
import { AffiliateFormEdit } from 'scenes/Affiliates/components/AffiliateForm';
import { TAB_DETAILS, TAB_ACTIONS_LOG } from 'scenes/Affiliates/constants';
import {
  useLoadAffiliateDetails,
  useReloadAffiliateDetails,
  useIsUpdating,
} from 'scenes/Affiliates/data';
import BusinessPartnerDetails from 'scenes/BusinessPartners/components/BusinessPartnerDetails';
import PartnerCompanyDetails from 'scenes/PartnerCompanies/components/PartnerCompanyDetails';

import ActionsLog from './ActionsLog';
import { AdjustTable } from './AdjustTable';
import ContactPersonSection from './Sections/ContactPersonSection';
import GeneralSection from './Sections/GeneralSection';
import Metadata from './Sections/MetaDataSection';
import RelatedEntitiesSection from './Sections/RelatedEntitiesSection';

const AffiliateDetails = ({
  id,
  onClose,
  onExited,
  open,
  onChange,
  activeTabId = TAB_DETAILS,
}) => {
  const isUpdating = useIsUpdating();
  const [childComponent, openChildComponent] = useChildComponent();
  const { data, isLoading, isRefetching } = useLoadAffiliateDetails(id);

  const handleChange = useReloadAffiliateDetails(id);

  const handleEdit = () => {
    openChildComponent(AffiliateFormEdit, {
      id: data.id,
      onSubmit: async () => {
        onChange && onChange();
      },
    });
  };

  const handleOpenPartnerCompany = (_e, { id }) => {
    openChildComponent(PartnerCompanyDetails, {
      id,
      onChange: handleChange,
    });
  };

  const handleOpenBusinessPartner = (_e, { id }) => {
    openChildComponent(BusinessPartnerDetails, {
      id,
      onChange: handleChange,
    });
  };

  return (
    <SidePage
      backdrop={false}
      onClose={onClose}
      onExited={onExited}
      loading={isLoading || isUpdating}
      fetching={isRefetching}
      open={open}
      size={'lg'}
    >
      <SidePage.Title
        title={'Affiliate'}
        entityId={id}
        subTitle={data?.name}
      />

      <SidePage.Tabs activeTabId={activeTabId}>
        <SidePage.Tab label={TAB_DETAILS}>
          <SidePage.NewBody wrapper={true} dense={true}>
            <SidePage.NewContent withSidebar={true}>
              {!isEmpty(data) ? (
                <React.Fragment>
                  <Section title={'General'}>
                    <GeneralSection
                      data={data}
                      onOpenCompany={handleOpenPartnerCompany}
                      onOpenPartner={handleOpenBusinessPartner}
                    />
                  </Section>
                  <Section
                    title={'Contact Person'}
                    isCollapsable={true}
                    initialOpen={false}
                  >
                    <ContactPersonSection data={data} />
                  </Section>
                  {data?.adjust?.length ? (
                    <Section
                      title={'Adjust Tokens'}
                      isCollapsable={true}
                      initialOpen={false}
                    >
                      <AdjustTable data={data.adjust} />
                    </Section>
                  ) : null}
                  <Section title={'Related Entities'}>
                    <RelatedEntitiesSection data={data} />
                  </Section>
                </React.Fragment>
              ) : null}
            </SidePage.NewContent>
            <SidePage.Sidebar
              actions={
                <AffiliateActions
                  limit={3}
                  data={data}
                  actions={get(data, 'actions')}
                  onEdit={handleEdit}
                  withViewLog={false}
                />
              }
            >
              <Metadata data={data} />
            </SidePage.Sidebar>
            {childComponent}
          </SidePage.NewBody>
        </SidePage.Tab>
        <SidePage.Tab label={TAB_ACTIONS_LOG}>
          <SidePage.NewBody dense={true}>
            <ActionsLog logs={data?.actionsLog} />
          </SidePage.NewBody>
        </SidePage.Tab>
      </SidePage.Tabs>
      <SidePage.Actions>
        <SidePage.CloseButton />
      </SidePage.Actions>
    </SidePage>
  );
};

export default AffiliateDetails;
